import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGraphneAPI, ResourceDetailsView } from '@nexelem/graphne-api';
import { GRAPHNE_RESOURCE_GROUP_LIST_TIMEFRAME_KEY } from './useGraphneResourcesGroupTimeframe';
import { useCustomToast } from '@nexelem/snackbar';
import { useCustomTranslation } from '../../hooks';
import { Trans } from 'react-i18next';
import { GRAPHNE_RESOURCE_SOFT_ASSIGNMENT_TASK_ID_KEY } from './useGraphneResourcesSoftAssignmentTaskId';
import { taskDetailsQueryKey } from './useGraphneTaskDetails';

type GraphneResourcesSoftUnassignMutationProps = {
  task: {
    id: number;
    name: string;
  };
  workersData: Pick<ResourceDetailsView, 'id' | 'name'>[];
};

export const useGraphneResourcesSoftUnassignMutation = (isBatch?: boolean) => {
  const api = useGraphneAPI();
  const queryClient = useQueryClient();
  const displayToast = useCustomToast();
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.assignWorkerModal');

  return useMutation({
    onSuccess: (_result, { workersData, task }) => {
      queryClient.invalidateQueries({ queryKey: [GRAPHNE_RESOURCE_GROUP_LIST_TIMEFRAME_KEY] });
      queryClient.invalidateQueries({ queryKey: [GRAPHNE_RESOURCE_SOFT_ASSIGNMENT_TASK_ID_KEY] });
      queryClient.invalidateQueries({ queryKey: taskDetailsQueryKey(task.id) });
      const workerNames = workersData.map(({ name }) => name).join(', ');
      displayToast({
        toastType: 'success',
        message: <Trans>{tWrapper('unassignSuccess', { productName: task.name, workers: workerNames })}</Trans>,
      });
    },
    onError: () => {
      displayToast({
        toastType: 'error',
        message: t('components.toasts.commonMessage.error'),
      });
    },
    mutationFn: ({ workersData, task }: GraphneResourcesSoftUnassignMutationProps) => {
      if (isBatch) {
        const softResourceBatchAssignment = workersData.map(({ id }) => {
          return {
            resourceId: id,
            batchId: task.id,
          };
        });

        return api.postGraphneResourcesSoftUnassignBatch({
          softResourceBatchAssignment,
        });
      }

      const softResourceAssignment = workersData.map(({ id }) => {
        return {
          resourceId: id,
          taskId: task.id,
        };
      });

      return api.postGraphneResourcesSoftUnassign({
        softResourceAssignment,
      });
    },
  });
};
